/**
 * The letterhead's jurisdiction line.
 *
 * PAIRED with `jurisdiction_line` in
 * services/voice-worker/src/respai_voice/pdf/render.py. The two print paths
 * render the same letterhead for the same department, so a department that
 * reads "Newfoundland & Labrador" on its downloaded PDF must not read "CA-NL"
 * on the copy it printed from the browser. Change one, change the other.
 *
 * `department.jurisdiction` holds an ISO 3166-2 subdivision code. A letterhead
 * is read by a fire chief and, later, by whoever handles a presumptive-cancer
 * claim — neither of them reads country codes.
 */

const SUBDIVISIONS: Readonly<Record<string, string>> = {
  "CA-AB": "Alberta",
  "CA-BC": "British Columbia",
  "CA-MB": "Manitoba",
  "CA-NB": "New Brunswick",
  "CA-NL": "Newfoundland & Labrador",
  "CA-NS": "Nova Scotia",
  "CA-NT": "Northwest Territories",
  "CA-NU": "Nunavut",
  "CA-ON": "Ontario",
  "CA-PE": "Prince Edward Island",
  "CA-QC": "Quebec",
  "CA-SK": "Saskatchewan",
  "CA-YT": "Yukon",
};

/** What the letterhead said for every department before RES-201, and what it
 *  falls back to when a department has no jurisdiction recorded. A blank line
 *  under the department name reads as a rendering fault; this reads as a
 *  record. */
export const JURISDICTION_FALLBACK = "Fire service incident record";

/** The sandbox form's dropdown options — the same 13 codes the two letterhead
 *  renderers can translate, so the form cannot produce a code that prints
 *  verbatim on a filed record (RES-208). */
export const SUBDIVISION_OPTIONS: ReadonlyArray<{ code: string; name: string }> =
  Object.entries(SUBDIVISIONS).map(([code, name]) => ({ code, name }));

export function jurisdictionLine(jurisdiction?: string | null): string {
  const text = (jurisdiction ?? "").trim();
  if (!text) return JURISDICTION_FALLBACK;
  // Anything unrecognised prints VERBATIM rather than being dropped: an
  // unexpected value is still the department's own answer to "who has
  // jurisdiction here", and blanking it would be the worse error.
  return SUBDIVISIONS[text.toUpperCase()] ?? text;
}
